import { useEffect, useRef, useState } from "react";
import type { RevisionRow } from "./api";

interface DocumentStatusUpdate {
  global_status: string;
  round_count: number;
}

interface UseDocumentEventsOptions {
  /** Called for every new revision the server pushes. */
  onRevision?: (rev: RevisionRow) => void;
  /** Called when the document's global_status / round_count changes. */
  onStatus?: (status: DocumentStatusUpdate) => void;
}

/**
 * Subscribe to /api/documents/{id}/events via the browser's EventSource.
 *
 * The server polls DynamoDB every 2s and sends new revisions as `event: revision`,
 * plus a periodic `event: status` snapshot. EventSource auto-reconnects on drop.
 */
export function useDocumentEvents(
  documentId: string | null,
  opts: UseDocumentEventsOptions,
) {
  const [connected, setConnected] = useState(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!documentId) return;
    const url = `/api/documents/${documentId}/events`;
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);

    es.addEventListener("revision", (ev) => {
      try {
        const rev: RevisionRow = JSON.parse((ev as MessageEvent).data);
        optsRef.current.onRevision?.(rev);
      } catch {
        /* ignore malformed event */
      }
    });

    es.addEventListener("status", (ev) => {
      try {
        const s: DocumentStatusUpdate = JSON.parse((ev as MessageEvent).data);
        optsRef.current.onStatus?.(s);
      } catch {
        /* ignore */
      }
    });

    return () => {
      es.close();
      setConnected(false);
    };
  }, [documentId]);

  return { connected };
}
